import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { useOrSigCreateMutation, useOrSigDeleteMutation } from '../../../../../graphql/react-operations';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { IconButton } from '@startuptools/ui/IconButton';
import { Table } from '@startuptools/ui/Table';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { EditableEmail } from '../../../../../components/editable-email/EditableEmail';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { DetailState, InputState, JuridicalKind } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { GenericError } from '@startuptools/ui/Error';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { OptionsRegistrationDocument } from '../../../OptionsRegistration.graphql';

export const OrStepAgreementSignatories = ({ warrantProgramId, orId }: { warrantProgramId: string; orId: string }) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const [showAddIdentity, setShowAddIdentity] = useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [doAddOrSig] = useOrSigCreateMutation({
    refetchQueries: [WarrantProgramDocument, OptionsRegistrationDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelOrSig] = useOrSigDeleteMutation({
    refetchQueries: [WarrantProgramDocument, OptionsRegistrationDocument],
  });
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId } });

  const or = warrantProgram.optionsRegistrations.find(or => or.id === orId);

  if (!or) {
    return <GenericError />;
  }

  return (
    <>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Who signs for the company?" />
        </Typography>

        <Box>
          <Button variant="outlined" onClick={() => setShowAddIdentity(true)}>
            <Trans id="Add Signatory" />
          </Button>
        </Box>

        <Table>
          <thead>
            <tr>
              <th />
              <th>Email</th>
              <th align="right" />
            </tr>
          </thead>
          <tbody>
            {or.orSignatories.map(sig => (
              <tr key={sig.id}>
                <td>
                  <Identity identity={sig.identity} />
                </td>
                <td>
                  <EditableEmail onChange={() => Promise.resolve()} refetchQueries={[WarrantProgramDocument]}>
                    {sig.identity.email}
                  </EditableEmail>
                </td>
                <td align="right">
                  <IconButton
                    variant="outlined"
                    color="danger"
                    onClick={() => doDelOrSig({ variables: { companyId, id: sig.id } })}
                  >
                    <DeleteIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button onClick={() => stepNext()}>
            <Trans id="Continue" />
          </Button>
        </Box>
      </Stack>
      {showAddIdentity && (
        <AddIdentity
          headline={<Trans id="Add Signatory" />}
          juridicalKinds={[JuridicalKind.Person]}
          identityExcludeList={or.orSignatories.map(or => or.identity)}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          detailFieldsSettings={{
            email: DetailState.Required,
          }}
          relations={{
            companyDirector: true,
          }}
          handleCreate={async identity => {
            await doAddOrSig({
              variables: {
                companyId,
                input: { optionsRegistrationId: orId, identityInput: identityToInput(identity) },
              },
            });
            setShowAddIdentity(false);
          }}
          onClose={() => {
            setShowAddIdentity(false);
          }}
        />
      )}
    </>
  );
};
